import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '../../../utils/ZiphyAPI';
import { Grid, Typography, List, ListItem, ListItemText, Link, Stack } from '@mui/material';
import { Address, Place, Insurance as ZiphyInsurance } from '../../../models/ziphy';
import { Patient as OutreachPatient } from '../../../models/core';
import InsuranceList from './insurance/InsuranceList';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { convertZMRtoOutreachInsurance } from '../../../utils/convertUtils';
import ZiphyPlaceInput from '../../book/ZiphyPlaceInput';
import AddressDialog, { AddressDialogMode } from './schedule/AddressDialog';
import { copyAction, ErrorListDialog, useErrorQueue, UseErrorQueueResult } from '../../ErrorListDialog';

interface ZCMPatientViewProps {
  zcmId: number;
  practiceId:number;
  patient?:OutreachPatient;
}

const ZCMPatientView: React.FC<ZCMPatientViewProps> = ({ zcmId, practiceId, patient }) => {

  const [placeValue, setPlaceValue] = React.useState<Place | null>(null);
  const [showPlaceEditor, setShowPlaceEditor] = React.useState(false);
  const errorQueue = useErrorQueue();

  const { isLoading, data: patientData } = useQuery({
    queryKey: ['patientData', zcmId],
    queryFn: async ({ queryKey }) => {
      const id = queryKey[1];
      if (!id) {
        return null;
      }
      const patientReq = await API.getPatientExpanded(Number(id));
      const addressesReq = await API.getAddresses(Number(id));
      const insuranceReq = await API.getInsuranceList(Number(id));
      const insurances = insuranceReq?.insurances?.items?.map((insurance: ZiphyInsurance) => convertZMRtoOutreachInsurance(insurance));
      return {
        patient: patientReq?.patient,
        addresses: addressesReq?.addresses?.items,
        insurance: insurances
      };
    },
    staleTime: 120_000,
    enabled: !!zcmId,
  });

  const updatePlace = React.useCallback(async (address: Place, practiceId:number, patientId:number, errorQueue:UseErrorQueueResult) => {
      if(!address) {
        errorQueue.enqueue({message:`No Place Was Selected`});
        return;
      }

      let addressId = address.id;
      if(!addressId) {
        const request = await API.createPlace(address, practiceId).catch((err) => { console.error(err) });
        const place = request as Place;
        if (!place) {
            errorQueue.enqueue({message:`Failed to create place: ${address.street}, ${address.city}, ${address.state}, ${address.zip}`})
            return;
        }
        addressId = place?.id;
      }

      if(addressId) {
          const req = await API.updatePatient({id:patientId, place_id:addressId});
          if(!req) {
              errorQueue.enqueue({message:`Failed to update patient with address. Please assign from Admin.`})
              return
          }
      }
  }, []);

  return (
    <div>
      {isLoading || !patientData?.patient ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
          <CircularProgress />
        </Box>
      ) : (
          <>
          <Grid container spacing={1.5} xs={12}>
            <Grid item xs={12}>
              <Typography variant="h6" ml={1}>Patient Information</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle1" ml={1.2}><b>First Name:</b> {patientData?.patient?.first_name || ''}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1"><b>Last Name:</b> {patientData?.patient?.last_name || ''}</Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="subtitle1" ml={1.2}><b>DOB:</b> {
                (patientData?.patient?.dob
                  ? `${patientData?.patient?.dob.month}/${patientData?.patient?.dob.day}/${patientData?.patient?.dob.year}`
                  : '') || ''}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1"><b>SAB:</b> {patientData?.patient?.gender}</Typography>
            </Grid>
            <Grid item xs={12}/>
            </Grid>

            <Typography variant="h6" ml={1}>Addresses</Typography>
            <List>
              {patientData?.addresses?.map((address: Address) => (
                <ListItem key={address.id}>
                  <ListItemText
                    primary={`${(address.line?.length || 0) >= 1 ? address.line?.at(0) : ''}${(address.line?.length || 0) >= 2 ? ', ' + address.line?.at(1) : ''}`}
                    secondary={`${address.city}, ${address.state} ${address.postalCode}`}
                  />
                </ListItem>
              ))}
            </List>
              <Box sx={{ mt: 3, ml: 1, mr: 1, mb: 3}}>
                  <Typography variant="h6" gutterBottom>Place</Typography>
                  <ZiphyPlaceInput
                      placeholder='Current Place'
                      practiceId={practiceId}
                      placeId={patientData?.patient?.place_id || undefined}
                      value={placeValue}
                      onChange={setPlaceValue}
                  />
                  <Stack direction={'row'} display="flex" justifyContent="space-between">
                    <Typography variant='body2' sx={{ visibility: placeValue == null ? 'visible' : 'hidden' }}>
                        <Link component='button' variant='body2' onClick={()=>setShowPlaceEditor(true)}>Create New Place</Link>
                    </Typography>
                    <Typography variant='body2' sx={{ visibility:
                        (placeValue && (placeValue?.id != patientData?.patient?.place_id || !placeValue?.id))  ? 'visible' : 'hidden' }}>
                        <Link component='button' variant='body2' onClick={
                          ()=>{
                            if(!placeValue || !patientData?.patient?.id) {
                              return;
                            }
                            updatePlace(placeValue, practiceId, patientData.patient.id, errorQueue);
                          }
                          }>Update Place</Link>
                    </Typography>
                  </Stack>
              </Box>
              <InsuranceList readonly={true} size={'body2'} insurance={patientData?.insurance || []} title='Insurance' />
              </>
      )}
      {
          showPlaceEditor && <AddressDialog mode={AddressDialogMode.ChooseAddress} outreachPatient={patient}
              open={showPlaceEditor} practiceId={practiceId}
              onChange={(place) => {
                  setPlaceValue(place);
                  setShowPlaceEditor(false);
                }
              }
              patient={patientData?.patient || undefined}
              onClose={() => setShowPlaceEditor(false)}/>
      }
      {
        errorQueue.dialogOpen && <ErrorListDialog
            open={errorQueue.dialogOpen}
            onClose={errorQueue.clear}
            title={errorQueue.title || <b>Attention</b>}
            description={errorQueue.body || <b>The following issues have occured</b>}
            errors={errorQueue.errors}
            globalActions={[copyAction]}
        />
      }
    </div>
  );
}

export default ZCMPatientView;
